import { Link } from 'react-router-dom';
import {
  Scale,
  Activity,
  HeartPulse,
  CheckCircle,
  Calendar,
  Sparkles,
  TrendingDown,
  Stethoscope,
  Pill,
  Gauge,
  ShieldCheck,
  CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { SEO } from '../components/SEO';

const pricingOptions = [
  {
    label: 'First Visit',
    tag: 'Initial consultation',
    description: 'Comprehensive screening, baseline measurements, metabolic workup discussion, and personalised plan.',
    highlight: false,
    color: 'from-teal-500 to-teal-600',
    ring: 'ring-teal-200',
  },
  {
    label: 'Monthly Follow-Up',
    tag: 'Per visit, thereafter',
    description: 'Ongoing monthly reviews to track progress, adjust your plan, and keep you accountable.',
    highlight: false,
    color: 'from-emerald-500 to-emerald-600',
    ring: 'ring-emerald-200',
  },
  {
    label: 'Annual Plan',
    tag: 'Paid upfront for the year',
    description: 'Best value for committed patients — 12 months of ongoing monthly reviews and support included.',
    highlight: true,
    savings: 'Best Value',
    color: 'from-rose-500 to-orange-500',
    ring: 'ring-rose-200',
  },
];

const WHATSAPP_QUOTE_URL =
  "https://wa.me/27828951916?text=Hi%2C%20I%27d%20like%20a%20price%20quote%20for%20the%20Weight%20Loss%20%26%20Wellness%20programme.";

const whatYouGet = [
  {
    icon: Stethoscope,
    title: 'Clinical Screening',
    description: 'Every person is screened to evaluate whether weight-loss medicines are needed — we never prescribe by default.',
  },
  {
    icon: Activity,
    title: 'Full Metabolic Screen',
    description: 'Blood work and assessments to identify treatable causes of weight gain (thyroid, insulin resistance, hormonal, and more).',
  },
  {
    icon: Pill,
    title: 'Exclusive Medication Pricing',
    description: 'Preferential rates on weight-loss-related medications through our practice — should medication be indicated.',
  },
  {
    icon: HeartPulse,
    title: 'Ongoing Support',
    description: 'Monthly reviews with your GP to track your progress, adjust treatment, and keep you motivated.',
  },
];

const metabolicCauses = [
  'Thyroid dysfunction',
  'Insulin resistance & pre-diabetes',
  'Cortisol / stress imbalance',
  'Vitamin deficiencies (B12, D)',
  'Hormonal imbalance (PCOS)',
  'Lipid abnormalities',
];

export const WeightLoss = () => {
  return (
    <div className="min-h-screen" data-testid="weight-loss-page">
      <SEO
        title="Weight Loss & Wellness"
        description="Medically supervised weight loss and wellness programme at K&K Medical and Dental, Umzinto & Scottburgh. Includes full metabolic screening and exclusive medication pricing. Contact us via WhatsApp for current rates."
        keywords="weight loss Umzinto, weight loss Scottburgh, medical weight loss KZN, metabolic screening, GLP-1 weight loss, obesity doctor KZN, wellness programme"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 py-16 lg:py-24 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 text-white text-sm font-semibold mb-6 animate-fade-in-up backdrop-blur-sm">
            <Sparkles className="w-4 h-4" />
            Medically Supervised Weight Loss
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 animate-fade-in-up animation-delay-100">
            Weight Loss <br className="hidden sm:block" />
            <span className="text-emerald-200">& Wellness</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto mb-8 animate-fade-in-up animation-delay-200">
            A doctor-led programme that treats the real cause of your weight gain — not the symptom.
            Screening, metabolic investigation, and an honest plan tailored to you.
          </p>

          <div className="flex flex-wrap justify-center gap-3 animate-fade-in-up animation-delay-300">
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 bg-white text-teal-700 px-6 py-3 rounded-full font-semibold hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="hero-pricing-link"
            >
              <TrendingDown className="w-5 h-5" />
              See Pricing
            </a>
            <Link
              to="/booking#weight-loss"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-slate-800 transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="hero-book-link"
            >
              <Calendar className="w-5 h-5" />
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 lg:py-24 bg-white" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">Our Approach</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Weight Loss Done Properly
            </h2>
            <p className="text-slate-600 max-w-3xl mx-auto leading-relaxed">
              We don't hand out tablets. Every person starts with a proper clinical screening and a full
              metabolic workup so we can find — and treat — the real reason behind the weight gain.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whatYouGet.map((item, idx) => (
              <Card
                key={item.title}
                className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 rounded-2xl bg-white animate-fade-in-up"
                style={{ animationDelay: `${idx * 100}ms` }}
                data-testid={`approach-card-${idx}`}
              >
                <CardContent className="p-6">
                  <div className="bg-teal-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Metabolic Screening detail */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-teal-50 to-emerald-50" data-testid="metabolic-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-2xl mb-6">
                <Gauge className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
                Full Metabolic Screen
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Weight gain is rarely just about willpower. A metabolic screen helps us uncover treatable
                causes that are often missed — and lets us build a plan that actually works for your body.
              </p>
              <p className="text-sm text-teal-700 font-medium mb-6 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                Confidential, evidence-based, and explained to you in plain language.
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                {metabolicCauses.map((cause) => (
                  <div key={cause} className="flex items-center gap-3 p-3 bg-white rounded-xl shadow-sm">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-700 text-sm font-medium">{cause}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="animate-fade-in-up animation-delay-200">
              <Card className="border-0 shadow-xl rounded-3xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-6">
                  <div className="flex items-center gap-3">
                    <Pill className="w-6 h-6" />
                    <CardTitle className="text-xl text-white">Medication — Only If You Need It</CardTitle>
                  </div>
                  <p className="text-emerald-100 text-sm mt-2">
                    Our stance on weight-loss medication
                  </p>
                </CardHeader>
                <CardContent className="p-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Stethoscope className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Every patient is screened</h4>
                      <p className="text-slate-600 text-sm">
                        We evaluate whether medication is clinically appropriate for your situation — or
                        whether lifestyle and metabolic correction alone will do the job.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">Exclusive pricing on approved medicines</h4>
                      <p className="text-slate-600 text-sm">
                        If medication is indicated, our patients access preferential rates on all
                        weight-loss-related medications through our practice.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 lg:py-24 bg-white scroll-mt-24" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">Personalised Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Choose Your Plan
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Three options — one first-visit consultation, monthly follow-ups, or an annual plan. No hidden fees, no lock-ins.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {pricingOptions.map((opt, idx) => (
              <Card
                key={opt.label}
                className={`relative border-0 shadow-lg rounded-3xl overflow-hidden bg-white animate-fade-in-up ${
                  opt.highlight ? `ring-2 ${opt.ring} lg:scale-105` : ''
                }`}
                style={{ animationDelay: `${idx * 120}ms` }}
                data-testid={`pricing-card-${idx}`}
              >
                {opt.highlight && (
                  <div className="absolute top-4 right-4 bg-rose-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md z-10">
                    {opt.savings}
                  </div>
                )}
                <div className={`bg-gradient-to-r ${opt.color} text-white py-6 px-6`}>
                  <p className="text-white/90 text-sm uppercase tracking-widest font-semibold">
                    {opt.tag}
                  </p>
                  <h3 className="text-2xl font-bold mt-1">{opt.label}</h3>
                </div>
                <CardContent className="p-6 flex flex-col h-full">
                  <p className="text-slate-600 leading-relaxed mb-6 flex-1">{opt.description}</p>
                  <Button
                    asChild
                    className={`w-full rounded-full shadow-md hover:shadow-lg transition-all ${
                      opt.highlight
                        ? 'bg-rose-600 hover:bg-rose-700 text-white'
                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                    data-testid={`pricing-book-btn-${idx}`}
                  >
                    <a href={WHATSAPP_QUOTE_URL} target="_blank" rel="noopener noreferrer">Get a Quote</a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Annual value callout */}
          <div className="mt-12 animate-fade-in-up">
            <div className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 rounded-3xl p-6 sm:p-8 text-white text-center sm:text-left sm:flex sm:items-center sm:justify-between gap-6 shadow-xl">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest mb-3">
                  <Sparkles className="w-4 h-4" />
                  Best Value
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2">Annual Plan — 12 months of support</h3>
                <p className="text-white/90 leading-relaxed">
                  Perfect if you're committed to seeing this through. Unlimited monthly reviews included.
                </p>
              </div>
              <Button
                asChild
                className="bg-white text-rose-600 hover:bg-rose-50 rounded-full px-8 py-6 text-lg font-semibold shadow-lg flex-shrink-0"
                data-testid="annual-plan-btn"
              >
                <a href={WHATSAPP_QUOTE_URL} target="_blank" rel="noopener noreferrer">Get an Annual Plan Quote</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Your First 30 Days
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                num: '01',
                title: 'Book your first visit',
                body: 'Your initial consultation includes a full history, measurements, and metabolic bloods.',
                icon: Calendar,
              },
              {
                num: '02',
                title: 'Review your results',
                body: 'We sit down with your results and build a plan — lifestyle-first, medication only if it genuinely helps.',
                icon: Scale,
              },
              {
                num: '03',
                title: 'Monthly check-ins',
                body: 'Ongoing reviews (or included in the annual plan). We track progress, refine the plan, and keep you on target.',
                icon: TrendingDown,
              },
            ].map((step, i) => (
              <Card
                key={step.num}
                className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 rounded-2xl bg-white animate-fade-in-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-4xl font-bold text-teal-600/30">{step.num}</span>
                    <div className="bg-teal-100 rounded-xl p-2">
                      <step.icon className="w-5 h-5 text-teal-600" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-slate-900 to-slate-800" data-testid="weight-loss-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-teal-500/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
            <HeartPulse className="w-8 h-8 text-teal-400" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to take your health back?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            A straight-talking, evidence-based weight-loss programme — in Umzinto and Scottburgh.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              asChild
              className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="weight-loss-book-btn"
            >
              <Link to="/booking#weight-loss">Book a Consultation</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg transition-all duration-300"
              data-testid="weight-loss-contact-btn"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
